import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@campus/browser';
import { ENVIRONMENT_API_TOKEN, EnvironmentApiInterface } from '@campus/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PersonApi, TaskApi } from '../+api';
import {
  EvaluationScoreListOptionsInterface,
  PrintSize,
  TaskClassGroupInterface,
  TaskEduContentInterface,
  TaskGroupInterface,
  TaskStudentInterface,
} from '../+models';
import { DAL_OPTIONS, DalOptions } from '../../lib/dal.module';
import { TaskInterface } from './../+models/Task.interface';
import {
  CreateAdaptiveTaskResponseInterface,
  CreateEvaluationAndAdaptiveTaskResponseInterface,
  CreateEvaluationTaskConfigInterface,
  CreateEvaluationTaskResponseInterface,
  DeleteEvaluationTasksResultInterface,
  StartEvaluationTaskResultInterface,
  StopEvaluationTaskResultInterface,
  TaskAssigneeIdsInterface,
  TaskAssigneesInterface,
  TaskServiceInterface,
  UpdateEvaluationScoreListSubjectResponseInterface,
  UpdateTaskResultInterface,
  WithAssigneesInterface,
} from './task.service.interface';
@Injectable({
  providedIn: 'root',
})
export class TaskService implements TaskServiceInterface {
  constructor(
    private personApi: PersonApi,
    private taskApi: TaskApi,
    private http: HttpClient,
    @Inject(ENVIRONMENT_API_TOKEN) private api: EnvironmentApiInterface,
    @Inject(DAL_OPTIONS) private dalOptions: DalOptions,
    @Inject(WINDOW) private window: Window
  ) {}
  getAllForUser(userId: number): Observable<TaskInterface[]> {
    return this.personApi.getData(userId, 'tasks').pipe(map((res: { tasks: TaskInterface[] }) => res.tasks));
  }

  linkEduContent(taskId: number, eduContentId: number): Observable<TaskEduContentInterface> {
    return this.taskApi.linkEduContents(taskId, eduContentId);
  }

  updateTasks(
    userId: number,
    update: Partial<TaskInterface>[],
    studentIds?: number[]
  ): Observable<UpdateTaskResultInterface> {
    const body = {
      partialTasks: update,
      studentIds,
    };
    return this.taskApi.updateTasks(body);
  }

  createTask(userId: number, task: TaskInterface): Observable<TaskInterface> {
    return this.personApi.createTeacherTasks(userId, task) as unknown as Observable<TaskInterface>;
  }

  duplicateTask(taskId: number): Observable<TaskInterface> {
    return this.taskApi
      .duplicateTask(taskId)
      .pipe(map((response: { errors: []; success: { task: TaskInterface } }) => response.success?.task));
  }

  createEvaluationTask(
    task: TaskInterface,
    evaluationId: number,
    evaluationSubjectIds: number[],
    assignees: {
      taskClassGroups?: TaskClassGroupInterface[];
      taskGroups?: TaskGroupInterface[];
    } = {},
    evaluationScoreListOptions?: EvaluationScoreListOptionsInterface
  ): Observable<CreateEvaluationTaskResponseInterface> {
    return this.taskApi.createForEvaluation(
      { ...task, evaluationId, ...assignees },
      evaluationSubjectIds,
      evaluationScoreListOptions
    );
  }

  createEvaluationTaskForWizard(
    task: TaskInterface,
    assignees: TaskAssigneeIdsInterface,
    evaluationConfig: CreateEvaluationTaskConfigInterface
  ): Observable<CreateEvaluationTaskResponseInterface> {
    return this.taskApi.createEvaluationTask(task, assignees, evaluationConfig).pipe(
      map((response) => ({
        ...response,
        ...castAssignees(response),
      }))
    );
  }

  createEvaluationAndAdaptiveTask(
    task: TaskInterface,
    assignees: TaskAssigneeIdsInterface,
    evaluationConfig: CreateEvaluationTaskConfigInterface
  ): Observable<CreateEvaluationAndAdaptiveTaskResponseInterface> {
    return this.taskApi.createEvaluationAndAdaptiveTask(task, assignees, evaluationConfig).pipe(
      map((response) => ({
        ...response,
        adaptive: { ...response.adaptive, ...castAssignees(response.adaptive) },
        evaluation: { ...response.evaluation, ...castAssignees(response.evaluation) },
      }))
    );
  }

  deleteEvaluationTasks(taskIds: number[]): Observable<DeleteEvaluationTasksResultInterface> {
    return this.taskApi.destroyEvaluationTasks(taskIds);
  }

  deleteTasks(taskIds: number[]): Observable<UpdateTaskResultInterface> {
    return this.taskApi.destroyTasks(taskIds);
  }

  startEvaluationTask(taskId: number, studentIds?: number[]): Observable<StartEvaluationTaskResultInterface> {
    return this.taskApi.startEvaluationTask(taskId, studentIds);
  }

  stopEvaluationTask(taskId: number): Observable<StopEvaluationTaskResultInterface> {
    return this.taskApi.stopEvaluationTask(taskId);
  }

  updateAccess(
    userId: number,
    taskId: number,
    taskGroups: TaskGroupInterface[],
    taskStudents: TaskStudentInterface[],
    taskClassGroups?: TaskClassGroupInterface[]
  ): Observable<TaskInterface> {
    return this.http
      .put(
        `${this.api.APIBase}/api/Tasks/${taskId}/update-access`,
        {
          groups: taskGroups,
          classGroups: taskClassGroups,
          students: taskStudents,
        },
        {
          withCredentials: true,
        }
      )
      .pipe(
        map(
          (task: TaskInterface): TaskInterface => ({
            ...task,
            taskClassGroups: task.taskClassGroups.map((tCG) => castStartEndToDate(tCG)),
            taskGroups: task.taskGroups.map((tG) => castStartEndToDate(tG)),
            taskStudents: task.taskStudents.map((tS) => castStartEndToDate(tS)),
          })
        )
      );
  }

  printTask(taskId: number, withNames: boolean, size: PrintSize = 'A5') {
    const { apiBaseUrl } = this.dalOptions;
    this.window.open(`${apiBaseUrl}/api/tasks/paper-task-pdf?taskId=${taskId}&withNames=${withNames}&size=${size}`);
  }

  printSolution(taskId: number, size: PrintSize = 'A5') {
    const { apiBaseUrl } = this.dalOptions;
    this.window.open(`${apiBaseUrl}/api/tasks/paper-task-solution-pdf?taskId=${taskId}&size=${size}`);
  }

  updateEvaluationScoreListSubjects(
    taskId: number,
    evaluationSubjectIds: number[]
  ): Observable<UpdateEvaluationScoreListSubjectResponseInterface> {
    return this.taskApi.updateEvaluationScoreListSubjects(taskId, evaluationSubjectIds);
  }

  createAdaptiveTaskFromEvaluationTask(evaluationTaskId: number): Observable<CreateAdaptiveTaskResponseInterface> {
    return this.taskApi.createAdaptiveTaskFromEvaluationTask(evaluationTaskId).pipe(
      map((response) => ({
        ...response,
        ...castAssignees(response),
      }))
    );
  }
}

export function castStartEndToDate<T extends TaskClassGroupInterface | TaskGroupInterface | TaskStudentInterface>(
  assignee: T
): T {
  return {
    ...assignee,
    start: assignee.start ? new Date(assignee.start) : undefined,
    end: assignee.end ? new Date(assignee.end) : undefined,
  };
}

const castAssignees = (data: WithAssigneesInterface): TaskAssigneesInterface => {
  const casted = {
    taskClassGroups: data.taskClassGroups?.map((tCG) => castStartEndToDate(tCG)),
    taskGroups: data.taskGroups?.map((tG) => castStartEndToDate(tG)),
    taskStudents: data.taskStudents?.map((tS) => castStartEndToDate(tS)),
  };
  return casted;
};
